import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Divider from 'housecall-ui/dist/DesignSystem/DataDisplay/Divider/Divider';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import DialogContent from 'housecall-ui/dist/DesignSystem/Feedback/Dialog/DialogContent';
import Box from 'housecall-ui/dist/DesignSystem/Layout/Box/Box';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import { AmplitudeFlagKeys } from 'types/amplitudeFlags';

import useIsExperimentActive from 'components/Amplitude/useIsExperimentActive';
import PayWallBannerDescription from 'components/PayWall/PayWallBannerDescription';
import { useTrackEventsService } from 'components/Services/Analytics/useTrackEventsService';

import { toggleOrgFeature, updateAdditionalUsers } from './Redux/Actions';
import {
  getAdditionalLoginsFeature,
  getAdditionalUserFromPlanSummary,
  getAdditionalUserPriceV3,
  getExistingUsersCount,
  getFinalAdditionalUsers,
  getHasUnlimitedSeats,
  getIsDowngradingPlan,
  getNumberOfFreeSeats,
  getPayWallAddonsFeaturesV3 as getPayWallAddonsFeatures,
  shouldPersistBanner,
} from './Redux/Selectors';
import AddFeatures from './AddFeatures';
import AddTeamMembers from './AddTeamMembers';
import {
  PAY_WALL_VERSION,
  useDialogContentStyles,
  usePayWallDialogGridPaddingFixStyles,
  usePayWallDialogMainGridStyles,
} from './PayWallDialog';
import PayWallPlanSummary from './PayWallPlanSummary';
import PayWallPlanSummaryV2 from './PayWallPlanSummaryV2';

export default function PayWallAddonsContent(props: { isLoading?: boolean }) {
  const { isLoading = false } = props;
  const isNewSummaryActive = useIsExperimentActive(AmplitudeFlagKeys.billingNewPayWallPlanSummary);
  const dialogContentClasses = useDialogContentStyles({ isLoading });
  const gridClasses = usePayWallDialogMainGridStyles();
  const gridPaddingFixClasses = usePayWallDialogGridPaddingFixStyles();
  const persistBanner = useSelector(shouldPersistBanner);
  const payWallAddonsFeatures = useSelector(getPayWallAddonsFeatures);
  const additionalUsersRate = useSelector(getAdditionalUserPriceV3);
  const additionalUsers = useSelector(getFinalAdditionalUsers);
  const additionalUsersFromNewPlan = useSelector(getAdditionalUserFromPlanSummary);
  const additionalLoginsFeature = useSelector(getAdditionalLoginsFeature);
  const hasUnlimitedSeats = useSelector(getHasUnlimitedSeats);
  const existingUsersCount = useSelector(getExistingUsersCount);
  const numberOfFreeSeatsFromCurrentPlan = useSelector(getNumberOfFreeSeats);
  const isDowngradingPlan = useSelector(getIsDowngradingPlan);

  const dispatch = useDispatch();
  const { trackButtonClicked } = useTrackEventsService();

  const showAddFeatures = payWallAddonsFeatures.length > 0;
  const showAddTeamMembers = additionalLoginsFeature && !hasUnlimitedSeats;

  return (
    <DialogContent dividers={false} classes={dialogContentClasses}>
      {persistBanner && <PayWallBannerDescription />}
      <Grid container spacing={5} classes={gridClasses}>
        <Grid item xs={12} md={6} classes={gridPaddingFixClasses}>
          <Grid container direction="column">
            <Typography variant="h3">Add-ons</Typography>
            <Box my={2}>
              <Divider />
            </Box>
            {showAddTeamMembers && (
              <AddTeamMembers
                additionalUsers={additionalUsers}
                minimumAdditionalUsers={additionalUsersFromNewPlan.minimum}
                additionalUsersRate={additionalUsersRate}
                onChangeAdditionalUsers={(newValue) => dispatch(updateAdditionalUsers(newValue))}
                existingUsersCount={existingUsersCount || 1}
                numberOfFreeSeatsFromCurrentPlan={numberOfFreeSeatsFromCurrentPlan}
                isDowngradingPlan={isDowngradingPlan}
              />
            )}
            {showAddFeatures && (
              <AddFeatures
                features={payWallAddonsFeatures}
                onFeatureToggle={(featureName, featurePrice, enabled, quantity) => {
                  dispatch(toggleOrgFeature(featureName, featurePrice, enabled, quantity));
                  trackButtonClicked({ buttonName: 'add on selected', payWallVersion: PAY_WALL_VERSION });
                }}
              />
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} md={6} classes={gridPaddingFixClasses}>
          {isNewSummaryActive ? <PayWallPlanSummaryV2 /> : <PayWallPlanSummary />}
        </Grid>
      </Grid>
    </DialogContent>
  );
}
